import { useEffect, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer";
import PageShareBar from "./PageShareBar";
import ReadingProgress from "./ReadingProgress";
import CrisisBanner from "./CrisisBanner";

/**
 * Force every external link to open in a new tab, while leaving internal
 * SPA navigation in the same tab. Runs as a single global pass (plus a
 * MutationObserver) so it also covers lazy-loaded article/MDX bodies and
 * any future links without per-anchor wiring. mailto:/tel:/sms: links and
 * same-origin (internal) links are left untouched.
 */
function decorateExternalLinks(root: ParentNode) {
  const anchors = root.querySelectorAll<HTMLAnchorElement>("a[href]");
  anchors.forEach((a) => {
    const href = a.getAttribute("href");
    if (!href) return;
    if (/^(mailto:|tel:|sms:|#)/i.test(href)) return;

    let url: URL;
    try {
      url = new URL(a.href, window.location.href);
    } catch {
      return;
    }
    if (url.protocol !== "http:" && url.protocol !== "https:") return;
    if (url.origin === window.location.origin) return; // internal link

    if (a.target !== "_blank") a.target = "_blank";
    const rel = (a.getAttribute("rel") || "").split(/\s+/).filter(Boolean);
    if (!rel.includes("noopener")) rel.push("noopener");
    if (!rel.includes("noreferrer")) rel.push("noreferrer");
    a.setAttribute("rel", rel.join(" "));
  });
}

export default function SiteLayout({ children }: { children: ReactNode }) {
  const { pathname } = useLocation();

  useEffect(() => {
    decorateExternalLinks(document.body);
    const observer = new MutationObserver(() => {
      decorateExternalLinks(document.body);
    });
    observer.observe(document.body, { childList: true, subtree: true });
    return () => observer.disconnect();
  }, []);

  // Scroll-reveal: progressively fade sections in as they enter the
  // viewport. The hidden state lives under `.js-reveal` (added here), so
  // the markup stays fully visible for crawlers and no-JS clients. Re-runs
  // on every route change to pick up the new page's `.reveal` elements.
  useEffect(() => {
    const root = document.documentElement;
    const els = Array.from(
      document.querySelectorAll<HTMLElement>(".reveal:not(.is-visible)"),
    );
    if (els.length === 0) return;

    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (reduceMotion || typeof IntersectionObserver === "undefined") {
      els.forEach((el) => el.classList.add("is-visible"));
      return;
    }

    root.classList.add("js-reveal");
    const io = new IntersectionObserver(
      (entries, obs) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            obs.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -8% 0px", threshold: 0.05 },
    );
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [pathname]);

  return (
    <div className="min-h-screen flex flex-col">
      <a href="#main" className="skip-link">
        Skip to content
      </a>
      <ReadingProgress />
      <CrisisBanner />
      <Header />
      <main id="main" tabIndex={-1} className="flex-1 outline-none">
        {children}
        <PageShareBar
          label="Share this page"
          className="mx-auto w-full max-w-[820px] px-6 pb-16"
        />
      </main>
      <Footer />
    </div>
  );
}
